import { useState, FormEvent } from "react";

const serviceOptions = [
  "Towing",
  "Vehicle Recovery",
  "Heavy Equipment Transport",
  "Roadside Assistance",
  "Impound / Storage Inquiry",
  "Law Enforcement / Municipal",
  "Other",
];

export default function LeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = ["name", "phone", "email", "service", "details", "city"];
    fields.forEach((f) => {
      const el = form.elements.namedItem(f) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el) data.append(f, el.value);
    });
    try {
      await fetch("https://readdy.ai/api/form/d7bueouhe76khnho4rc0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setLoading(false);
  };

  return (
    <section id="contact" className="bg-[#0a0a0c] py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left copy */}
          <div className="lg:w-5/12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#D4AF37]"></div>
              <span className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase">Get a Response Fast</span>
            </div>
            <h2 className="text-white font-black text-4xl md:text-5xl leading-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Request Professional Recovery Service
            </h2>
            <p className="text-[#888] text-base leading-relaxed mb-8">
              Submit your details and a dispatcher will follow up promptly. For immediate assistance, call us directly — we're available 24 hours a day, every day of the year.
            </p>
            <a href="tel:9516822003" className="inline-flex items-center gap-3 mb-8 cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm group-hover:bg-[#D4AF37]/20 transition-colors">
                <i className="ri-phone-fill text-[#D4AF37] text-xl"></i>
              </div>
              <div>
                <p className="text-[#888] text-xs uppercase tracking-wider">Call Direct</p>
                <p className="text-white font-bold text-xl">(951) 682-2003</p>
              </div>
            </a>
            <div className="space-y-3">
              {[
                { icon: "ri-map-pin-line", text: "3777 Placentia Lane, Riverside, CA 92501" },
                { icon: "ri-time-line", text: "Open 24 Hours — 7 Days a Week" },
                { icon: "ri-building-line", text: "Additional facility: Moreno Valley, CA" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-[#888] text-sm">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-[#D4AF37]`}></i>
                  </div>
                  {item.text}
                </div>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className="lg:w-7/12 w-full">
            <div className="bg-[#111113] border border-[#2a2a2c] rounded-lg p-8 border-t-2 border-t-[#D4AF37]">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#D4AF37]/10 rounded-full">
                    <i className="ri-checkbox-circle-line text-[#D4AF37] text-3xl"></i>
                  </div>
                  <h3 className="text-white font-bold text-2xl mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Request Received</h3>
                  <p className="text-[#888] text-sm">A dispatcher will follow up with you shortly. For immediate assistance, call (951) 682-2003.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="exclusive-towing-lead-form" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#888] text-xs uppercase tracking-wider mb-2">Full Name *</label>
                      <input name="name" required type="text" placeholder="Your name" className="w-full bg-[#1a1a1c] border border-[#2a2a2c] text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder-[#444]" />
                    </div>
                    <div>
                      <label className="block text-[#888] text-xs uppercase tracking-wider mb-2">Phone Number *</label>
                      <input name="phone" required type="tel" placeholder="(xxx) xxx-xxxx" className="w-full bg-[#1a1a1c] border border-[#2a2a2c] text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder-[#444]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#888] text-xs uppercase tracking-wider mb-2">Email Address</label>
                    <input name="email" type="email" placeholder="your@email.com" className="w-full bg-[#1a1a1c] border border-[#2a2a2c] text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder-[#444]" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-xs uppercase tracking-wider mb-2">Service Needed *</label>
                    <select name="service" required className="w-full bg-[#1a1a1c] border border-[#2a2a2c] text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-[#D4AF37] transition-colors cursor-pointer">
                      <option value="">Select a service...</option>
                      {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#888] text-xs uppercase tracking-wider mb-2">Vehicle / Situation Details</label>
                    <textarea name="details" rows={3} maxLength={500} placeholder="Describe your vehicle and situation..." className="w-full bg-[#1a1a1c] border border-[#2a2a2c] text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder-[#444] resize-none"></textarea>
                  </div>
                  <div>
                    <label className="block text-[#888] text-xs uppercase tracking-wider mb-2">City / Location *</label>
                    <input name="city" required type="text" placeholder="e.g. Riverside, Moreno Valley..." className="w-full bg-[#1a1a1c] border border-[#2a2a2c] text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder-[#444]" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black font-bold text-base py-4 rounded-sm tracking-wider uppercase hover:bg-[#c49f27] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                    {loading ? "Sending..." : "Request Immediate Service →"}
                  </button>
                  <p className="text-center text-[#555] text-xs">Secure inquiry &bull; Rapid response &bull; Professional service</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
